import React, { useEffect } from 'react';
import { useToasts } from 'react-toast-notifications';

import H2 from 'components/H2';
import { initializeEcho, getPrivateUserChannel } from 'utils/echo';
import { EVENT_STUDY_PEOPLE_ADDED, PUSHER_EVENT_STUDY_PEOPLE_ADDED } from 'utils/constants';
import ToastSuccess from 'containers/ParticipantsPage/components/ToastSuccess';

/**
 * Payload broadcast on `private-App.User.{userId}` as the `study.people-added` event.
 * Mirrors the backend event structure from AddPeopleToStudyJob.
 *
 * @typedef {Object} PeopleAddedPayload
 * @property {number} studyId - ID of the study that panelists were added to
 * @property {number} userId - ID of the user who initiated the operation
 * @property {number} peopleAddedCount - Actual count added (after deduplication)
 * @property {number} requestedCount - Original count requested
 * @property {'completed'|'failed'} status - Status of the background job
 * @property {string} message - Human-readable message
 * @property {string} [studyName] - Display name of the study
 */

/**
 * Toast body for failed people-added operations.
 *
 * @param {Object} props
 * @param {string} props.message - Error message from the backend
 * @returns {JSX.Element}
 */
function PeopleAddedFailedToast({ message }) {
    return (
        <>
            <H2>Failed to add panelists to study</H2>
            <div className="color-text-secondary fs-body-14">{message}</div>
        </>
    );
}

/**
 * Toast body for showing duplicate panelist info.
 *
 * @param {Object} props
 * @param {number} props.duplicateCount - Number of panelists that were duplicates
 * @returns {JSX.Element}
 */
function DuplicatePanelistsToast({ duplicateCount }) {
    const text = duplicateCount === 1 ? 'panelist was' : 'panelists were';
    return (
        <>
            <H2>Note: Some panelists were already in the study</H2>
            <div className="color-text-secondary fs-body-14">
                {duplicateCount} {text} already in the study and skipped.
            </div>
        </>
    );
}

/**
 * Subscribes to the user's broadcast channel for study people-added notifications.
 * Mount once near the App root, INSIDE QueryClientProvider and ToastProvider.
 *
 * This context listens for async notifications when large batches of panelists
 * (>1000) are added to studies. Small batches (≤1000) are handled synchronously
 * and don't trigger these notifications.
 *
 * @param {Object} props
 * @param {number|string|undefined} props.userId - Authenticated user ID. When
 *     unset (logged-out / pre-auth) the subscription effect is skipped.
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode} `children` unchanged.
 */
export function StudyNotificationsProvider({ userId, children }) {
    const { addToast } = useToasts();

    useEffect(() => {
        if (!userId) return undefined;
        const pusher = initializeEcho();
        if (!pusher) return undefined;

        /**
         * @param {PeopleAddedPayload} payload
         */
        const handlePeopleAdded = payload => {
            // Validate payload structure before any side effects
            if (!payload || typeof payload !== 'object') return;
            if (payload.status !== 'completed' && payload.status !== 'failed') return;
            if (typeof payload.studyId !== 'number' || payload.studyId <= 0) return;
            if (typeof payload.message !== 'string') return;

            // For completed status, validate counts
            if (payload.status === 'completed') {
                if (
                    typeof payload.peopleAddedCount !== 'number' ||
                    !Number.isFinite(payload.peopleAddedCount) ||
                    payload.peopleAddedCount < 0
                ) {
                    return;
                }
                if (
                    typeof payload.requestedCount !== 'number' ||
                    !Number.isFinite(payload.requestedCount) ||
                    payload.requestedCount < 0
                ) {
                    return;
                }
            }

            if (payload.status === 'completed') {
                // Show success toast with count and link to study
                addToast(
                    <ToastSuccess
                        studyId={payload.studyId}
                        studyName={payload.studyName}
                        newCount={payload.peopleAddedCount}
                    />,
                    {
                        appearance: 'success',
                        big: true,
                        autoDismiss: true
                    }
                );

                // If there were duplicates, show an additional info toast
                const duplicateCount = payload.requestedCount - payload.peopleAddedCount;
                if (duplicateCount > 0) {
                    addToast(<DuplicatePanelistsToast duplicateCount={duplicateCount} />, {
                        appearance: 'info',
                        big: true,
                        autoDismiss: true
                    });
                }

                // Dispatch custom event for class-based StudyPage to refresh
                window.dispatchEvent(
                    new CustomEvent(EVENT_STUDY_PEOPLE_ADDED, {
                        detail: { studyId: payload.studyId }
                    })
                );
            } else if (payload.status === 'failed') {
                // Show error toast
                addToast(<PeopleAddedFailedToast message={payload.message} />, {
                    appearance: 'error',
                    big: true,
                    autoDismiss: false
                });
            }
        };

        const channel = pusher.subscribe(getPrivateUserChannel(userId));
        channel.bind(PUSHER_EVENT_STUDY_PEOPLE_ADDED, handlePeopleAdded);

        return () => {
            channel.unbind(PUSHER_EVENT_STUDY_PEOPLE_ADDED, handlePeopleAdded);
        };
    }, [userId, addToast]);

    return children;
}
